import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChevronIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TagIcon,
  TrashIcon,
} from '@/components/catalog/icons';
import { ProductInclusionsModal } from '@/components/catalog-admin/ProductInclusionsModal';
import { ProductInclusionPresetsModal } from '@/components/products/ProductInclusionPresetsModal';
import { ProductFormModal } from '@/components/products/ProductFormModal';
import { ProductXeroBadge } from '@/components/products/ProductXeroBadge';
import { productPushReport } from '@/components/products/product-xero';
import { XeroPushReportBanner } from '@/components/xero/XeroRowPushControl';
import type { XeroRowPushReport } from '@/lib/xero';
import {
  InlineTravelProduct,
  useTravelProductCatalogue,
} from '@/components/products/InlineTravelProduct';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateOnly, formatDecimalString, orDash, toDateInputValue } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import type {
  CampaignInclusion,
  Paginated,
  Product,
  ProductListSort,
  ProductXeroPush,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import {
  Badge,
  Button,
  ColumnManager,
  Combobox,
  type ComboboxOption,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  type FilterableTHProps,
  type FrozenSide,
  Input,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  type SortDirection,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  ValuePills,
  namePills,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

const PER_PAGE = 20;
/** Fewer than a full page: enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 6;

/*
 * 🔴 **`GET /products` ACCEPTS `sort` AND `order`. THIS BLOCK SAID IT DID NOT,
 * AND THE CLAIM IS SUPERSEDED — 2026-09-13.**
 *
 * *SUPERSEDED, and every clause of it is now false:* ***"`GET /products` accepts
 * FOUR query params and no others — `page`, `perPage`, `search`, `active` —
 * measured against `listProductsQuerySchema` in the backend rather than inferred
 * from a 200. … No `sort` param exists — every list is ordered `id DESC`
 * server-side — so no column here declares a `sortKey`, which is what keeps
 * `FilterableTH` from rendering Ascending/Descending."***
 *
 * The live accepted set is `page`, `perPage`, `search`, `active`, **`sort`** —
 * `productName`, `unitPrice`, `createdAt`, `modifiedAt`, `id` — and **`order`**.
 *
 * 🔴 **SORT IS WIRED NOW — 2026-10-09, owner's instruction.** Every column with
 * a funnel offers Ascending / Descending, held in the URL as `?sort=` /
 * `?order=` and sent as `sort` / `order` (members: `PRODUCT_SORT_MEMBERS`). With
 * no sort chosen nothing is sent, so the server's default order stands. ⚠️
 * *SUPERSEDED 2026-10-09, the next paragraph and the `/intakes` one after it:
 * `/intakes` now takes `sort` / `order` too.* The `unitPrice` NULLs-first trap
 * is unchanged and is what an ascending price sort shows.
 *
 * ⚠️ **NOTHING ON THIS PAGE HAS BEEN CHANGED TO USE THEM, DELIBERATELY.** No
 * column declares a `sortKey` today, so `FilterableTH` still renders no
 * Ascending/Descending here — but that is now **an unbuilt feature, not an
 * impossible one**, and the difference matters to whoever reads this next.
 * Sortable headers on this table are a real option and need a decision, not a
 * contract change.
 *
 * ⚠️ **AND ONE TRAP TRAVELS WITH THEM: `unitPrice` IS NULLABLE AND THE SERVER
 * COALESCES NOTHING.** `?sort=unitPrice&order=asc` therefore OPENS on the
 * UNPRICED products — MariaDB's natural NULLs-first — and `COALESCE(unit_price,
 * 0)` is named in the backend source as the WRONG fix, because it files an
 * unpriced product alongside a genuinely free one. `null` is unpriced, `"0.00"`
 * is a real price of zero; a sort must not quietly merge them.
 *
 * ⚠️ **`/intakes` TAKES NEITHER `sort` NOR `order` AND ITS SCHEMA IS
 * `.strict()`.** So a shared catalog sort control emitted on all three catalog
 * screens works on Products and Cycles and **422s Intakes, taking its whole
 * table out**. Do not infer one catalog endpoint's parameters from another's —
 * that inference is exactly what this block got wrong in the other direction.
 *
 * Still true, and unaffected by any of the above: `active` is the only column
 * FILTER on this page that can reach the server, and Category, Travel product,
 * Unit price, Qty and Created are page-scoped because **there is no filter param
 * to send**, not because nobody has wired one yet. Client-side sorting is still
 * skipped too — it would only reorder the current page, which looks like a
 * whole-set sort but isn't, and that objection is about the PAGE, not about the
 * API, so it survives the correction above intact.
 *
 * ⚠️ *SUPERSEDED with it, the whole strict-versus-stripped paragraph:* ***"This
 * block used to say `?sort=` is 'silently stripped', and the two backend
 * artefacts DISAGREE about that … under both readings the accepted set is the
 * four params above, and an invented one is never a working filter."*** The
 * dispute was about what happens to `?sort=` on this endpoint, and **`?sort=` is
 * now an accepted parameter**, so the question has no subject left. The rule it
 * was invoked for is untouched and still binds: **never build a control for a
 * filter that does not exist** — measure the schema, do not infer it from a 200.
 *
 * `search` is real, accepted, and still NOT wired to the Product column, for
 * two independent reasons. It is a substring match on `product_name` while a
 * column filter emits exact distinct values, so ticking "SEED Summer Camp"
 * would send `?search=SEED Summer Camp` and also match "SEED Summer Camp Plus";
 * and the panel's search box already writes that param, so wiring the column to
 * it would give one query key two writers that overwrite each other with
 * nothing on screen saying which won.
 */

type ActiveFilter = 'all' | 'true' | 'false';

const ACTIVE_OPTIONS: { value: ActiveFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'true', label: 'Active only' },
  { value: 'false', label: 'Inactive only' },
];

function activeParamFrom(raw: string | undefined): ActiveFilter {
  return raw === 'true' || raw === 'false' ? raw : 'all';
}

/*
 * ⚠️ **THE PANEL-HEADER STATUS FILTER IS A `Combobox` — owner's instruction,
 * 2026-09-13: every non-table-column filter dropdown becomes searchable,
 * REGARDLESS OF OPTION COUNT.** Three options plainly do not need a search box.
 * What they need is to behave like every other filter in the app, so a reader
 * never has to discover which dropdowns can be typed into and which cannot.
 *
 * 🔴 **THE TWO PRIMITIVES SPELL "NO FILTER" DIFFERENTLY, AND THAT IS THE ONE
 * THING THIS CONVERSION HAD TO GET RIGHT.** `ActiveFilter` spells it as a REAL
 * member, `'all'`; a `Combobox` spells it as `''` plus `emptyOptionLabel`. So
 * the value is mapped in BOTH directions at the call site — `'all'` renders as
 * `''`, and `''` is read back as `'all'` — and the two derivations below are
 * computed FROM `ACTIVE_OPTIONS` rather than retyped beside it.
 *
 * ⛔ **Deriving, not copying, is load-bearing.** `ACTIVE_OPTIONS` is also cited
 * by the `productActive` column spec as the source of its option ORDER (Active
 * then Inactive, deliberately not alphabetised). A second hand-written list here
 * would agree with it the day it was written and drift the first time somebody
 * relabels a status — leaving the dropdown and the column funnel spelling one
 * status two ways, with nothing failing.
 */
const ALL_STATUSES_LABEL =
  ACTIVE_OPTIONS.find((option) => option.value === 'all')?.label ?? 'All statuses';

const ACTIVE_PICKER_OPTIONS: ComboboxOption[] = ACTIVE_OPTIONS.filter(
  (option) => option.value !== 'all',
).map(({ value, label }) => ({ value, label }));

/**
 * `active` takes exactly one value, but `useTableFilters` sends `selected[0]`
 * for a checkbox — so a second tick would change nothing server-side while
 * the UI still showed two ticked. Collapsing to the most recent tick makes it
 * behave like the radio it really is. Unticking drops the key, which is how
 * "either status" is expressed.
 */
function singleValued(props: FilterableTHProps): FilterableTHProps {
  const { filter } = props;
  if (!filter) return props;
  return {
    ...props,
    filter: {
      ...filter,
      onChange: (next) => filter.onChange({ ...next, selected: next.selected.slice(-1) }),
    },
  };
}

/*
 * Date columns group and label by bare 'YYYY-MM-DD' rather than a localised
 * form: the option list sorts by label, and ISO-8601 is the one date format
 * that sorts chronologically as plain text. It also keeps every date a string
 * throughout — `new Date('2026-05-31')` is midnight UTC and renders as 30 May
 * west of Greenwich.
 */
const PRODUCT_COLUMNS: ColumnSpec<Product>[] = [
  { key: 'productName', label: 'Product', get: (row) => row.productName },
  { key: 'productCategory', label: 'Category', get: (row) => row.productCategory },
  {
    key: 'travelProduct',
    label: 'Travel product',
    /*
     * The NAME, so the funnel groups by the product a reader recognises. `null`
     * falls through to Is empty / Is not empty, which is the useful question
     * here — "which products carry travel with them" is exactly what the
     * tracker's derived Travel column now depends on.
     */
    get: (row) => row.travelProduct?.productName ?? null,
  },
  {
    key: 'productActive',
    label: 'Status',
    // The one server-backed column on this page.
    scope: 'server',
    param: 'active',
    get: (row) => (row.productActive ? 'true' : 'false'),
    format: (value) => (value === 'true' ? 'Active' : 'Inactive'),
    /*
     * DECLARED, not accumulated out of the loaded rows.
     *
     * ⚠️ Without this the funnel can only ever offer statuses the current page
     * happens to hold. `product_active` is a BOOLEAN, so a catalogue whose first
     * page of 20 is all active offers no "Inactive" checkbox at all — and the
     * popover gives no hint that a value is missing, so "there are no inactive
     * products" and "you are looking at a page that has none" read identically.
     * A retired product is exactly the kind of row somebody opens this filter to
     * find.
     *
     * ⚠️ Declaring options is a claim that the API accepts every value listed
     * (see `ColumnSpec.options`), and this one is already proven: the status
     * dropdown above the table writes this same column filter and has always
     * sent both `?active=true` and `?active=false` through this param.
     *
     * Labels are the two strings `format` already produces, so the checkbox and
     * the cell cannot spell one status two ways. Order is Active then Inactive —
     * `options` is deliberately NOT alphabetised, and this is the order
     * `ACTIVE_OPTIONS` lists them in.
     *
     * Nothing here needs the retired-master treatment the other pages carry: the
     * vocabulary is a boolean, not a table somebody can deactivate a row in.
     */
    options: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Inactive' },
    ],
  },
  {
    key: 'unitPrice',
    label: 'Unit price',
    numeric: true,
    /*
     * The raw DECIMAL string is the filter value, never a float. `null` is "no
     * price set" and falls through to Is empty / Is not empty; "0.00" is a real
     * price of zero and is a value like any other. Collapsing the two would
     * price an unpriced product at nothing.
     */
    get: (row) => row.unitPrice,
    format: (value) => formatDecimalString(value),
  },
  {
    key: 'qtyOrdered',
    label: 'Qty',
    numeric: true,
    // Stringified so a genuine 0 stays a value rather than reading as empty.
    get: (row) => String(row.qtyOrdered),
  },
  {
    key: 'createdAt',
    label: 'Created',
    // Grouped by day: `createdAt` is a real timestamp, so full precision would
    // make every row its own distinct value.
    get: (row) => toDateInputValue(row.createdAt),
  },
];

/**
 * Column key → the `/products` sort member it orders by. Every funnel column has
 * one; the two spec-less columns (inclusions, actions) have none.
 */
const PRODUCT_SORT_MEMBERS = {
  productName: 'productName',
  productCategory: 'productCategory',
  travelProduct: 'travelProduct',
  productActive: 'productActive',
  unitPrice: 'unitPrice',
  qtyOrdered: 'qtyOrdered',
  createdAt: 'createdAt',
} satisfies Partial<Record<string, ProductListSort>>;

const SORT_MEMBER_BY_KEY: Partial<Record<string, ProductListSort>> = PRODUCT_SORT_MEMBERS;
const SORT_MEMBERS: ReadonlySet<string> = new Set(Object.values(PRODUCT_SORT_MEMBERS));

/** The URL's sort, or `null` when none (or an unrecognised one) is named. */
function parseSort(params: URLSearchParams): { key: ProductListSort; direction: SortDirection } | null {
  const raw = params.get('sort');
  if (raw === null || !SORT_MEMBERS.has(raw)) return null;
  return { key: raw as ProductListSort, direction: params.get('order') === 'desc' ? 'desc' : 'asc' };
}

/*
 * ## ONE ordered key list drives the header, the body and the skeleton
 *
 * `PRODUCT_COLUMNS` covers SEVEN of the nine columns, because it is the FILTER
 * model and two of the nine have no filter to model: `inclusionsOffered` is not
 * a field on `Product` at all (it is read one product at a time — see the block
 * below), and `actions` is not data. **Keying the table off `PRODUCT_COLUMNS`
 * would therefore have silently dropped both**, which is the whole reason the
 * order lives here instead and the filter spec stays where it was.
 *
 * ⚠️ **A key with no `ColumnSpec` is not a special case anywhere downstream**,
 * and that is deliberate rather than lucky: `useTableFilters.headerFor` already
 * answers an unknown key with a plain `{ label: key }` carrying **no `filter`**
 * — which is exactly a plain header — so the two spec-less columns need no
 * branch in the hook, no placeholder spec, and no filter control over values the
 * page does not hold. They need exactly two things of their own, and both are
 * below: a LABEL (`SPECLESS_LABELS`, so the column manager names them rather
 * than showing a raw key) and a WIDTH.
 */
const ACTIONS_KEY = 'actions';
const OFFERING_KEY = 'inclusionsOffered';
/**
 * The product's Xero Item status (owner 2026-10-28). Spec-less like the
 * offering column: it carries no filter, so it needs no `ColumnSpec`.
 */
const XERO_KEY = 'xeroSync';

/** Every column this table can render, in its DECLARED order. */
const COLUMN_KEYS = [
  'productName',
  'productCategory',
  'travelProduct',
  'productActive',
  'unitPrice',
  'qtyOrdered',
  'createdAt',
  XERO_KEY,
  OFFERING_KEY,
  ACTIONS_KEY,
] as const;

/**
 * ⛔ **`actions` is LOCKED** — it carries Edit, Inclusions, Presets and Delete, so it is
 * the one column whose absence removes a CAPABILITY rather than a fact, and a
 * missing affordance is indistinguishable from a removed feature (the companies
 * board already cost this repo that report). It stays listed in the manager,
 * disabled, never omitted, so its presence there does not read as the feature
 * having been lost.
 */
const LOCKED_KEYS = [ACTIONS_KEY] as const;

/**
 * Columns the page renders with `pinned`, which is `sticky right-0`.
 *
 * ⚠️ **NOT THE SAME SET AS `LOCKED_KEYS`, even though today they hold the same
 * one key.** Locked is about hiding and reordering; pinned is about which edge a
 * cell already occupies, and the two answer different questions of both the
 * layout hook and the manager. Passing `LOCKED_KEYS` where this belongs is what
 * made an identifying column unfreezable on the pages that lock one — `product`
 * here is the obvious freeze-left candidate, and it must stay freezable however
 * many locks it collects.
 *
 * It is handed to three places and they must agree:
 * - `useColumnLayout`, so `setFrozen` refuses the one column whose edge is
 *   already decided rather than storing a second, conflicting position rule;
 * - `ColumnManager`, so that column shows no freeze buttons at all;
 * - `useFrozenOffsets`, so the pinned lane's rendered width is COUNTED and the
 *   next right-frozen column stacks beside it instead of underneath it.
 */
const PINNED_KEYS = [ACTIONS_KEY] as const;

/**
 * Why a locked column cannot be hidden, in the reader's own terms.
 *
 * ⚠️ **A why-this-is-disabled sentence, which the no-helper-text rule keeps by
 * name** — a bare disabled tickbox makes a locked column and a broken switch
 * identical. It has to be per key rather than one fixed string: this page locks
 * the actions lane because it carries CAPABILITIES, and the next lock here will
 * be for some other reason entirely.
 */
function lockReasonOfColumn(key: string): string | undefined {
  return key === ACTIONS_KEY
    ? 'Edit, Inclusions, Presets and Delete are only here, so this column always shows'
    : undefined;
}

/** One page, one scope string — the same vocabulary the saved filters use. */
const COLUMN_SCOPE = 'products';

/** The two columns `PRODUCT_COLUMNS` has no entry for. */
const SPECLESS_LABELS: Record<string, string> = {
  [XERO_KEY]: 'In Xero',
  [OFFERING_KEY]: 'Inclusions offered',
  [ACTIONS_KEY]: 'Actions',
};

/**
 * The header's own label, wherever it lives.
 *
 * Read from `PRODUCT_COLUMNS` first so a spec-backed column cannot be named one
 * thing in the header and another in the column manager — the manager's job is
 * to let somebody find a column they have hidden, which needs the two strings to
 * be the same string.
 */
function labelOfColumn(key: string): string {
  return PRODUCT_COLUMNS.find((column) => column.key === key)?.label ?? SPECLESS_LABELS[key] ?? key;
}

/**
 * ONE width table: the Tailwind class the header carries AND the px it reserves,
 * kept as a PAIR so the two cannot drift apart.
 *
 * ⚠️ **The px half is what the table's floor is summed from, and it has to be a
 * number rather than a class.** Tailwind v4 scans source text, so a class built
 * from a variable emits no rule at all — the class lands in the DOM and does
 * nothing, which looks exactly like a table that chose not to reserve the space.
 * The floor therefore goes through `style`; see `ProductsTable`.
 *
 * ✅ That also retires the hazard the old code warned about at length: the floor
 * was a literal `min-w-[1496px]` written into THREE unequally-indented
 * `<Table>`s, so a careless replace-all updated two of them and left the third
 * narrower than its own header row. There is now one number, computed once.
 *
 * ⚠️ **It is still a FLOOR, not a sum of rendered widths.** This table has no
 * `table-fixed` and no `<colgroup>`, so a `w-*` on a `<th>` is a request the
 * content can outvote — the floor is what actually reserves the space. What
 * changed is that it now moves on its own when a column is added, widened **or
 * HIDDEN**, instead of having to be remembered.
 *
 * ⚠️ **`productName` is 360 on the owner's instruction, and 180 was DERIVED.**
 * The column carried no width at all: it was the one unconstrained column, so at
 * the old 1316 floor it got whatever the other eight left it — 1316 − 1136 =
 * 180px. That is the number this is twice of, and it is why "double the width"
 * had no literal to double until it was written here. A product name WRAPS
 * (`[overflow-wrap:anywhere]` in `ProductName`), so the cost of 180 was never a
 * clipped name — it was `Sales - Business School Festivals: …` stacking three
 * lines deep and setting the height of every row beside it. Same reason and same
 * fix as the companies list's `w-80`.
 */
const COLUMN_WIDTHS: Record<string, { className: string; px: number }> = {
  productName: { className: 'w-[360px]', px: 360 },
  productCategory: { className: 'w-40', px: 160 },
  travelProduct: { className: 'w-44', px: 176 },
  productActive: { className: 'w-28', px: 112 },
  unitPrice: { className: 'w-40', px: 160 },
  qtyOrdered: { className: 'w-20', px: 80 },
  createdAt: { className: 'w-40', px: 160 },
  [XERO_KEY]: { className: 'w-28', px: 112 },
  [OFFERING_KEY]: { className: 'w-48', px: 192 },
};

/**
 * The actions lane is the one column whose width depends on the signed-in role,
 * so it cannot sit in the table above. All nine at `shown` sum to **1528**.
 * ⚠️ *SUPERSEDED: **1496**, at `w-32`/128 — "byte for byte the floor this page
 * carried before the sum was computed".* That floor was the arithmetic of THREE
 * action buttons; the Presets action made it four.
 *
 * ⚠️ **This width is arithmetic, not taste, and it is `sm` `iconOnly` buttons
 * that set it**: four at `w-8` (32) with three `gap-1.5` (6) is 128, plus `TD`'s
 * `px-3` twice (24) = **152**, so 128 would have clipped the lane. `w-40` (160)
 * is the next step up that holds it. **Move this with any button added to or
 * removed from the lane** — nothing recomputes it and nothing fails when it is
 * wrong: the buttons simply overhang a frozen, `sticky right-0` cell.
 */
const ACTIONS_WIDTHS = {
  shown: { className: 'w-40', px: 160 },
  hidden: { className: 'w-20', px: 80 },
};

function widthOf(key: string, showActions: boolean): { className: string; px: number } {
  if (key === ACTIONS_KEY) return showActions ? ACTIONS_WIDTHS.shown : ACTIONS_WIDTHS.hidden;
  return COLUMN_WIDTHS[key] ?? { className: '', px: 0 };
}

/**
 * What a frozen cell needs from the layout — empty when the column is not
 * frozen, so an unfrozen cell carries no `left`/`right` at all rather than a
 * falsy one (`Table` says why: it would become a positioned cell painting over
 * the table's collapsed borders for no reason).
 */
type FrozenCellProps = { frozen?: FrozenSide; frozenOffset?: number };

/*
 * ## Inclusions offered is read ONE PRODUCT AT A TIME
 *
 * ⚠️ A rate-limit decision, not a shortcut: `GET /products/{id}/inclusions`
 * answers for one product only — no bulk endpoint, no count on the product
 * row, and the GET's query schema is an empty `.strict()` object (even
 * `?isActive=true` is a 422). A column filled in for every row would cost one
 * request per row — up to 100 at this endpoint's page-size ceiling — and this
 * list re-queries on every debounced keystroke, page turn and status change,
 * so an eager column would spend that budget repeatedly, not once. The limit
 * is per IP, shared office-wide. ⚠️ *SUPERSEDED: "120 requests/60s."* It is
 * **12,000/60s** (corrected 2026-09-17) — **one request per row on a list that
 * re-queries as you type is the wrong SHAPE, and that is what this decision
 * rests on**, not on the size of the bucket.
 *
 * So "which products offer inclusions?" is deliberately not answered at the
 * page level — it needs a backend count or bulk read. Until then this is a
 * per-row read the person asks for: bounded by clicks, cached for the session,
 * never fired by rendering. Helper text is banned app-wide, so the only
 * surviving explanation is the `Check` button's `title` — do not reinstate a
 * visible note above the table; that `title` is load-bearing, not decoration.
 *
 * `undefined` (never asked) is deliberately distinct from a loaded empty set:
 * the first is unknown, the second is a real answer — and today it's the
 * answer for every product, because the link table is empty and seeding it is
 * a business decision nobody has taken. Collapsing the two would report the
 * whole catalogue as offering nothing without having asked once.
 */
type Offering =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; inclusions: CampaignInclusion[] };

/** Short enough for a table cell; the server's own words go on the `title`. */
function offeringError(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.status === 404) return 'No answer — this product may have just been deleted.';
    return caught.message;
  }
  return 'Could not reach the server.';
}

/**
 * Local rather than in `@/components/catalog/icons`, which is shared by three
 * catalog pages — this action is products-only today. Same conventions as its
 * neighbours: `currentColor` and `aria-hidden`, so the accessible name comes
 * from the button.
 */
function ChecklistIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="h-4 w-4"
    >
      <path d="m3 6 1.5 1.5L7.5 4.5" />
      <path d="m3 12 1.5 1.5L7.5 10.5" />
      <path d="m3 18 1.5 1.5L7.5 16.5" />
      <path d="M11 6h10" />
      <path d="M11 12h10" />
      <path d="M11 18h10" />
    </svg>
  );
}

/**
 * The quantity-band ladder — steps rising to the right. Local for the same
 * reason as {@link ChecklistIcon}: this action is products-only today.
 *
 * ⚠️ Deliberately NOT a second checklist. Offers and presets are two different
 * dialogs sitting one button apart, and two similar glyphs in one lane is how
 * somebody opens the wrong one and edits the wrong whole-set replace.
 */
function BandsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="h-4 w-4"
    >
      <path d="M3 19h5v-4H3z" />
      <path d="M9.5 19h5v-8h-5z" />
      <path d="M16 19h5V5h-5z" />
    </svg>
  );
}

/**
 * The identifying cell: name, then the code and the add-on marker beside it.
 *
 * ⚠️ **Mounted BOTH in the desktop table's `productName` cell and in the
 * sub-`md` card list**, which is why the add-on marker lives here rather than in
 * a column of its own — one edit reaches both breakpoints, and a signal
 * reachable only above `md` is indistinguishable from one that was removed.
 */
function ProductName({ row }: { row: Product }) {
  const hasSecondLine = Boolean(row.productCode) || row.isAddOn;
  return (
    <div className="min-w-0">
      <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">{row.productName}</p>
      {hasSecondLine && (
        /* Wraps, so at 320px a long code never squeezes the badge off the row. */
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1">
          {row.productCode && (
            <span className="min-w-0 truncate text-xs text-fg-muted tabular">
              {row.productCode}
            </span>
          )}
          {row.isAddOn && <AddOnBadge />}
        </div>
      )}
    </div>
  );
}

/**
 * Marks a product whose lines are deliberately absent from the Client
 * Deliverables Tracker.
 *
 * ⚠️ **This is a BADGE and not a column, and that is a decision.** The flag is
 * true on a small minority of the catalogue, so a column would be a heading over
 * an empty cell on nearly every row — and it would cost a `ColumnSpec`, a
 * `COLUMN_KEYS` entry and a `COLUMN_WIDTHS` entry that moves the table's floor,
 * to answer a question the name cell can answer in place.
 *
 * ⛔ **A server-backed funnel for it is NOT available either**: `/products` takes
 * `page`, `perPage`, `search`, `active`, `sort` and `order`, and that schema is
 * `.strict()` — so a `scope: 'server'` spec on this would 422 naming the key and
 * take the whole table out, not merely fail to filter.
 *
 * ⚠️ **It exists at all because the flag's effect is INVISIBLE on every other
 * screen, and it fails in the vanishing direction**: a wrongly flagged product
 * makes the tracker shorter, and a shorter page reads as work nobody has quoted
 * rather than as rows suppressed. Without a marker here the only way to learn
 * that a product is flagged is to open its edit dialog, one product at a time.
 *
 * The label is the business's own word so the row can be SCANNED for; the
 * consequence, which no badge-length label can carry, goes on the `title` — the
 * same routing the `hasOpenTask` column uses, and a FACT rather than advice.
 * Nothing a reader needs lives there alone: being flagged is carried by the
 * badge's presence, which is visible on touch where a `title` is not.
 */
function AddOnBadge() {
  return (
    <Badge
      tone="neutral"
      variant="outline"
      title="Its lines get no row of their own on Client Deliverables"
    >
      Add-on
    </Badge>
  );
}

/**
 * A word plus a dot, as plain text rather than a filled pill — nearly every row
 * is active, and a column of identical green pills shouted the common case. The
 * same treatment the catalogue's status column uses.
 */
function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={
        active
          ? 'inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-fg-secondary'
          : 'inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-fg-muted'
      }
    >
      <span
        aria-hidden="true"
        className={active ? 'h-1.5 w-1.5 rounded-full bg-success' : 'h-1.5 w-1.5 rounded-full bg-fg-subtle'}
      />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

/**
 * `null` is "no price set" and "0.00" is a real price of zero. Collapsing the
 * two would silently price an unpriced product at nothing, so the em dash is
 * load-bearing rather than cosmetic.
 */
function PriceCell({
  value,
  unitLabel,
}: {
  value: string | null;
  /** `Product.unitLabel`: shown as "per <unit>" beside a set price. */
  unitLabel?: string | null | undefined;
}) {
  if (value === null) {
    return (
      <span className="text-xs text-fg-subtle" title="No price set">
        —
      </span>
    );
  }
  const unit = unitLabel?.trim() ?? '';
  return (
    <span className="tabular">
      {formatDecimalString(value)}
      {unit !== '' && <span className="text-fg-muted"> per {unit}</span>}
    </span>
  );
}

/**
 * What one product offers, in four states that must read differently.
 *
 * The empty state matters most: today it's the answer for every product,
 * because the link table was deliberately seeded with nothing — which
 * products offer what is a business decision, not a gap. It must read as
 * "nobody has set this up", never as a failure. Hence "Not set up yet" in a
 * neutral tone, with the full sentence on the title, rather than a bare zero
 * or an em dash.
 *
 * No quantity appears here, deliberately — "5 campuses" is a fact about a
 * quote LINE, not the product, so showing a unit here would invent a number
 * the record doesn't carry.
 */
function OfferingCell({
  product,
  state,
  onCheck,
}: {
  product: Product;
  state: Offering | undefined;
  onCheck: () => void;
}) {
  if (state === undefined) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={onCheck}
        aria-label={`Show which campaign inclusions ${product.productName} offers`}
        /* A cost, not an unfinished feature — keep this one sentence here
           rather than duplicating it onto the header, where the two would
           drift out of sync. */
        title="One request per product — read only when you ask."
      >
        Check
      </Button>
    );
  }

  if (state.status === 'loading') {
    // The shape of the answer — a badge and its line of names — not a spinner.
    return (
      <div className="grid gap-1">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-2.5 w-28" />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className="text-2xs text-danger [overflow-wrap:anywhere]"
          title={`Couldn’t read what ${product.productName} offers. ${state.message}`}
        >
          Couldn&rsquo;t read this
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCheck}
          aria-label={`Try again to show which campaign inclusions ${product.productName} offers`}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (state.inclusions.length === 0) {
    return (
      <span
        title={`Nothing set up for ${product.productName} yet — an administrator chooses these.`}
      >
        <Badge tone="neutral" variant="outline">
          Not set up yet
        </Badge>
      </span>
    );
  }

  const names = state.inclusions.map((inclusion) => inclusion.name);
  return (
    <div className="min-w-0" title={names.join(', ')}>
      {/* The count is in the label, so it reads as a quiet tag beside "Not set
          up yet" rather than as a status. */}
      <Badge tone="neutral" variant="outline">
        {names.length} offered
      </Badge>
      <ValuePills items={namePills(names)} className="mt-1" />
    </div>
  );
}

/**
 * `createdBy` is an opaque provenance string ('1', '3', 'api') that collides
 * with real user ids, so it is shown raw and never resolved to a name.
 */
function SourceLine({ value }: { value: string | null }) {
  if (!value) return null;
  return <p className="mt-0.5 truncate text-2xs text-fg-subtle">by {value}</p>;
}

/*
 * ✅ **THE LOCAL `HeadCell` IS GONE — `FilterableTH` FORWARDS `frozen` AND
 * `frozenOffset` NOW (2026-09-12).** *SUPERSEDED: a module-local `TH` +
 * `ColumnFilter` composition, and its comment explaining that
 * `FilterableTHProps` declared "label / numeric / className / pinned / wrap /
 * filter and NOTHING else", so the two freezing props spread onto it were
 * accepted by the type checker — JSX spread of a non-fresh object skips
 * excess-property checking — and then silently dropped.*
 *
 * That is fixed in the primitive, so the copy is deleted rather than kept: a
 * local re-spelling of a shared component is what silently misses the shared
 * component's next improvement, which is how this page came to be one `wrap`
 * behind on the day the prop landed.
 *
 * ⚠️ *SUPERSEDED 2026-10-09: every funnel column now carries a URL-backed
 * `filter.sort` (see `PRODUCT_SORT_MEMBERS`), so `aria-sort` IS emitted on
 * those headers — the "arrives for free" half below is what happened.* The
 * original paragraph:
 *
 * ⚠️ **`aria-sort` is unchanged, and it is unchanged because NO COLUMN HERE
 * DECLARES A `sortKey`** (`GET /products` has no `sort` param at all). So
 * `filter.sort` is always `undefined` and `FilterableTH` emits no `aria-sort`
 * at all — exactly what the deleted copy did, for exactly the same reason: an
 * `aria-sort="none"` announces a control that is deliberately not rendered.
 * ⚠️ **The day a sort key lands on this page it now arrives for free**, which
 * is the other half of not keeping a copy.
 */

function ProductsTableHead({
  showActions,
  headerFor,
  keys,
  frozenFor,
  headRowRef,
}: {
  showActions: boolean;
  headerFor: (key: string) => FilterableTHProps;
  /** Visible columns in layout order — the SAME array the body maps. */
  keys: readonly string[];
  frozenFor: (key: string) => FrozenCellProps;
  /**
   * Handed to `useFrozenOffsets`, which measures the RENDERED width of each
   * cell. There is no `table-fixed` and no `<colgroup>` here, so a `w-*` on a
   * `<th>` is a request the content may outvote — the DOM is the only honest
   * source for a frozen column's offset.
   */
  headRowRef: RefObject<HTMLTableRowElement | null>;
}) {
  return (
    <THead>
      <TR ref={headRowRef}>
        {keys.map((key) => {
          const width = widthOf(key, showActions);

          /* Pinned to the right edge of the scroller — at its floor this table
             is far wider than a content area of viewport minus the ~256px
             sidebar, so unpinned the actions column sits past the edge at every
             width this app is tested at and its buttons become unclickable.
             Pinned in TWO places now, this header and the body `<TD>`; the
             skeleton reads the same key list, so the column can no longer jump
             sideways when the data lands.

             ⚠️ It takes NO `frozenFor`: `pinned` already IS `frozen="right"` at
             offset 0, and applying both would put two mechanisms on one cell.
             It is still counted as a frozen right column by the MEASURER —
             `useFrozenOffsets` is handed `PINNED_KEYS` and folds them in
             itself — so a user-frozen right column stacks beside it rather than
             underneath it. */
          if (key === ACTIONS_KEY) {
            return (
              <TH key={key} numeric pinned className={width.className}>
                {SPECLESS_LABELS[ACTIONS_KEY]}
              </TH>
            );
          }

          /* A plain TH: the offering is not a field on `Product`, so
             `useTableFilters` cannot see it and a filter here would be a control
             over values the page does not hold. It is an ordinary column to the
             LAYOUT, though — hideable, movable and freezable like any other. */
          if (key === OFFERING_KEY || key === XERO_KEY) {
            return (
              <TH key={key} className={width.className} {...frozenFor(key)}>
                {SPECLESS_LABELS[key]}
              </TH>
            );
          }

          return (
            <FilterableTH
              key={key}
              {...headerFor(key)}
              className={width.className}
              {...frozenFor(key)}
            />
          );
        })}
      </TR>
    </THead>
  );
}

/**
 * One shimmering cell, keyed the same way the real row is.
 *
 * ⚠️ **It reads the SAME key list and the SAME freeze props as the header and
 * the body**, which is what stops a hidden or frozen column from shifting the
 * moment the data lands. The old version listed nine `<TD>`s positionally, so a
 * column hidden in the header would have left the shimmer a column wider — and
 * the mismatch would have looked like a rendering glitch rather than a bug.
 */
function skeletonCell(key: string, actions: number, frozenFor: (key: string) => FrozenCellProps) {
  const frozen = frozenFor(key);
  switch (key) {
    case 'productName':
      return (
        <TD key={key} {...frozen}>
          <div className="grid gap-1.5">
            <Skeleton className="h-3 w-48" />
            <Skeleton className="h-2.5 w-20" />
          </div>
        </TD>
      );
    case 'productCategory':
      return (
        <TD key={key} {...frozen}>
          <Skeleton className="h-3 w-24" />
        </TD>
      );
    case 'travelProduct':
      return (
        <TD key={key} {...frozen}>
          <Skeleton className="h-3 w-28" />
        </TD>
      );
    case 'productActive':
      return (
        <TD key={key} {...frozen}>
          <Skeleton className="h-4 w-16" />
        </TD>
      );
    case 'unitPrice':
      return (
        <TD key={key} numeric {...frozen}>
          <Skeleton className="ml-auto h-3 w-16" />
        </TD>
      );
    case 'qtyOrdered':
      return (
        <TD key={key} numeric {...frozen}>
          <Skeleton className="ml-auto h-3 w-6" />
        </TD>
      );
    case 'createdAt':
      return (
        <TD key={key} {...frozen}>
          <Skeleton className="h-3 w-24" />
        </TD>
      );
    case XERO_KEY:
      return (
        <TD key={key} {...frozen}>
          <Skeleton className="h-4 w-14" />
        </TD>
      );
    case OFFERING_KEY:
      return (
        <TD key={key} {...frozen}>
          {/* The unasked state is a small button, so that is what shimmers. */}
          <Skeleton className="h-6 w-16" />
        </TD>
      );
    case ACTIONS_KEY:
      return (
        <TD key={key} numeric pinned>
          <div className="flex justify-end gap-1.5">
            {Array.from({ length: actions }, (_, j) => (
              <Skeleton key={j} className="h-6 w-6" />
            ))}
          </div>
        </TD>
      );
    default:
      return null;
  }
}

/** `actions` is the number of row buttons the signed-in role will actually get,
 *  so the shimmer is the shape of the real row rather than a fixed guess. */
function TableSkeleton({
  actions,
  keys,
  frozenFor,
}: {
  actions: number;
  keys: readonly string[];
  frozenFor: (key: string) => FrozenCellProps;
}) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>{keys.map((key) => skeletonCell(key, actions, frozenFor))}</TR>
      ))}
    </TBody>
  );
}

/**
 * `TableWrap` + the table's floor, in one place for all three mounts.
 *
 * ⚠️ **The floor is an INLINE STYLE, and that half is forced and permanent.** A
 * Tailwind class built from a number emits no rule at all — Tailwind v4 scans
 * SOURCE TEXT, so `min-w-[${n}px]` lands in the DOM and does nothing, which
 * looks exactly like a table that chose not to reserve the space.
 *
 * ✅ **IT SITS ON THE `<Table>` ITSELF NOW — `Table` TAKES A `style`.**
 * *SUPERSEDED: a bare `<div style={{ minWidth }}>` between `TableWrap` and
 * `Table`, and its comment that "`Table` takes `className` but no `style`, so
 * the number cannot go on the table itself".* That wrapper existed for one
 * missing prop and nothing else; three adopting pages each grew their own copy
 * of it, which is why the prop landed. The rendered result is unchanged: the
 * table is the box that overflows `TableWrap`'s `overflow-x-auto` and produces
 * the horizontal scroll, at the same used width the literal `min-w-[1496px]`
 * produced, from a number that shrinks as columns are hidden.
 */
function ProductsTable({ minWidth, children }: { minWidth: number; children: ReactNode }) {
  return (
    <TableWrap frozenHeader>
      <Table style={{ minWidth }}>{children}</Table>
    </TableWrap>
  );
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li
          key={i}
          className="flex items-start justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
        >
          <div className="grid flex-1 gap-1.5">
            <Skeleton className="h-3 w-40" />
            <Skeleton className="h-2.5 w-20" />
            <Skeleton className="h-2.5 w-28" />
            <Skeleton className="h-6 w-16" />
          </div>
          <Skeleton className="h-4 w-14" />
        </li>
      ))}
    </ul>
  );
}

export function ProductsPage() {
  const { ability } = useAuth();

  /*
   * The read gate. A hidden sidebar row is an optimisation and never a
   * guarantee — the URL is typeable, the command palette offers the page, and a
   * permission set can change under an access token that lasts days — so the
   * page answers for itself.
   *
   * ⚠️ **`canRead`, never `isAdmin`.** `products` writes are admin-only however
   * generous the set is (`ADMIN_WRITE_MODULES`), and that has nothing to do with
   * who may LOOK: reads are open to every role, so gating this page on the tier
   * would hide a working screen from every viewer. The set is the rule here.
   *
   * ⚠️ DEGRADES OPEN: only an explicit `none` scope denies. See `can()` in
   * `@/lib/permissions`, which carries the reasoning.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /products` answers the
   * same to everybody, so nothing here may be worded as though the data were
   * protected. This is a view control.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. The admin write gates below are untouched.
   */
  const canReadModule = ability.canRead('products');

  const [result, setResult] = useState<Paginated<Product> | null>(null);
  const [search, setSearch] = useRememberedState('search', '');
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const sort = useMemo(() => parseSort(new URLSearchParams(searchKey)), [searchKey]);
  const sortMember = sort?.key ?? null;
  const sortDirection = sort?.direction ?? null;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /** `null` closed, `{}` create, `{ product }` edit. */
  const [formState, setFormState] = useState<{ product?: Product } | null>(null);
  const [inclusionsFor, setInclusionsFor] = useState<Product | null>(null);
  /*
   * Separate state from `inclusionsFor`, not a mode on it: the two dialogs write
   * two different whole-set-replace endpoints, and one `null`-able "which
   * product" shared between them is how a mode flag eventually opens the wrong
   * one over the right product.
   */
  const [presetsFor, setPresetsFor] = useState<Product | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<ReactNode>(null);
  /** The last product save's Xero Item push, when it failed. */
  const [pushReport, setPushReport] = useState<XeroRowPushReport | null>(null);

  /*
   * What each product offers, keyed by product id — read on demand, kept for
   * the session. Keyed by id rather than row index so paging, searching or
   * reordering never re-spends a request or shows one product's answer under
   * another's name. A missing key means "not asked" — not the same as an
   * empty array (see the `Offering` block above).
   */
  const [offerings, setOfferings] = useState<Record<number, Offering>>({});
  /** In-flight reads, so a double-click cannot spend two requests on one row. */
  const offeringRequests = useRef(new Map<number, AbortController>());

  // Every outstanding per-row read is abandoned when the page goes away.
  useEffect(() => {
    const inFlight = offeringRequests.current;
    return () => {
      for (const controller of inFlight.values()) controller.abort();
      inFlight.clear();
    };
  }, []);

  /**
   * One request, for one product, because the person asked for it.
   *
   * ⚠️ Never call this from render or from an effect over `rows` — that's the
   * eager version this design refuses (see the `Offering` block above).
   */
  const checkOffering = useCallback((productId: number) => {
    const inFlight = offeringRequests.current;
    // Already asking. Re-entering would spend a second request for one answer.
    if (inFlight.has(productId)) return;

    const controller = new AbortController();
    const { signal } = controller;
    inFlight.set(productId, controller);
    setOfferings((current) => ({ ...current, [productId]: { status: 'loading' } }));

    void api
      .productInclusions(productId, signal)
      .then((response) => {
        if (signal.aborted) return;
        setOfferings((current) => ({
          ...current,
          [productId]: { status: 'ready', inclusions: response.data },
        }));
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setOfferings((current) => ({
          ...current,
          [productId]: { status: 'error', message: offeringError(caught) },
        }));
      })
      .finally(() => {
        if (signal.aborted) return;
        inFlight.delete(productId);
      });
  }, []);

  /**
   * Re-read one product after its offering was just rewritten.
   *
   * The PUT is a full replace and returns the new set, but `onSaved` carries
   * nothing, so the row is re-read rather than guessed at. Dropping the
   * cached entry first is what lets `checkOffering` run again — one request
   * after a deliberate write, not a request per row on every render.
   */
  const refreshOffering = useCallback(
    (productId: number) => {
      offeringRequests.current.get(productId)?.abort();
      offeringRequests.current.delete(productId);
      checkOffering(productId);
    },
    [checkOffering],
  );

  const rows = useMemo(() => result?.data ?? [], [result]);
  const filters = useTableFilters(rows, PRODUCT_COLUMNS);

  /*
   * Column layout — order, hidden set and the two frozen edges — stored AGAINST
   * THE ACCOUNT. `use-column-layout` carries the reasoning: not `localStorage`
   * (per browser, per device, and thrown away with site data), not the URL (a
   * shared link would impose the sender's hidden columns on the recipient), and
   * the HIDDEN keys are stored rather than the visible ones so a column this
   * table gains later can never default to hidden.
   */
  const layout = useColumnLayout(COLUMN_SCOPE, COLUMN_KEYS, LOCKED_KEYS, PINNED_KEYS);
  const headRowRef = useRef<HTMLTableRowElement | null>(null);

  /*
   * ⛔ **ACTIONS IS FORCED LAST, whatever the stored order says.** `lockedKeys`
   * stops the actions column ITSELF being moved; it does not stop another column
   * being dropped after it — `moveTo` splices at an index and the index past the
   * end is reachable from the manager. `pinned` is `sticky right-0`, so an
   * actions cell that is not last floats OVER the columns after it: buttons
   * drawn on top of live data, with nothing erroring. Appending unconditionally
   * also repairs a stored layout that somehow has it hidden.
   */
  const renderKeys = useMemo(
    () => [...layout.visibleKeys.filter((key) => key !== ACTIONS_KEY), ACTIONS_KEY],
    [layout.visibleKeys],
  );

  /*
   * Depends on `layout.frozenSideOf` (a `useCallback` inside the hook) rather
   * than on `layout` — the hook memoises the controller now, but naming the one
   * member the measurer actually needs is still what keeps its `ResizeObserver`
   * from being torn down and rebuilt on unrelated state changes.
   */
  const layoutFrozenSideOf = layout.frozenSideOf;

  const initialLoad = loading && !result;

  /*
   * ✅ **THE ACTIONS LANE IS DECLARED TO THE MEASURER AS `PINNED_KEYS`, NOT
   * FAKED AS RIGHT-FROZEN.** *SUPERSEDED: a local `frozenSideOf` wrapper
   * answering `'right'` for `ACTIONS_KEY` before delegating.* `pinned` IS
   * `frozen="right"` at offset 0, so leaving the lane out of the measurement
   * hands a user-frozen right column an offset of 0 as well and that column
   * sits UNDERNEATH the pinned actions rather than beside it — a silent
   * overlap, not an error. The hook folds the pinned keys in itself now, so the
   * fact is stated once here and nothing has to re-derive it.
   *
   * ✅ **AND THE MOUNT DISCRIMINATOR IS GONE WITH IT.** *SUPERSEDED: a
   * `tableMount` string read as `void tableMount` inside that wrapper purely so
   * its identity changed when one of this page's three `<Table>` mounts
   * replaced another.* Each mount renders its OWN header row, so a swap
   * exchanged the element `headRowRef` points at while the key list and the
   * frozen sides stayed identical — nothing told the hook to look again, and
   * its observer stayed attached to the DETACHED row with every offset holding
   * the skeleton's widths. **`useFrozenOffsets` tracks the observed element
   * itself now**, which is the fix this workaround was standing in for.
   *
   * ⚠️ **The three mounts are LEFT AS THEY ARE.** Unifying them is the other
   * repair for the same hazard and it is no longer needed for this; the
   * headers-only branch exists so the filter that emptied the page stays
   * reachable to undo.
   */
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    renderKeys,
    layoutFrozenSideOf,
    PINNED_KEYS,
  );
  const frozenFor = useCallback(
    (key: string): FrozenCellProps => {
      if (key === ACTIONS_KEY) return {};
      const side = layoutFrozenSideOf(key);
      if (side === undefined) return {};
      return { frozen: side, frozenOffset: frozenOffsets[key] ?? 0 };
    },
    [layoutFrozenSideOf, frozenOffsets],
  );

  /*
   * The status dropdown and the Status column filter are one control in two
   * places, not two fighting over one param: the dropdown reads and writes
   * the column filter's state. Kept because below `md` the table — and every
   * column filter with it — is replaced by cards.
   */
  const statusFilter = filters.headerFor('productActive').filter;
  const active = activeParamFrom(filters.serverParams.active);

  const headerFor = (key: string): FilterableTHProps => {
    const props =
      key === 'productActive' ? singleValued(filters.headerFor(key)) : filters.headerFor(key);
    const member = SORT_MEMBER_BY_KEY[key];
    if (member === undefined || props.filter === undefined) return props;
    return {
      ...props,
      filter: {
        ...props.filter,
        sort: {
          direction: sortMember === member ? sortDirection : null,
          onSort: (direction) => changeSort(member, direction),
        },
      },
    };
  };

  /** Writes the sort into the URL and returns to page 1 of the re-ordered list. */
  function changeSort(member: ProductListSort, direction: SortDirection) {
    setPage(1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sort', member);
        next.set('order', direction);
        return next;
      },
      { replace: true },
    );
  }

  function changeActive(next: ActiveFilter) {
    statusFilter?.onChange({ selected: next === 'all' ? [] : [next], emptiness: 'any' });
  }

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return api
        .listProducts(
          {
            page,
            perPage: PER_PAGE,
            // Blank keys are omitted, never sent empty: `?search=` and
            // `?active=` are 422s server-side rather than "no filter".
            ...(search ? { search } : {}),
            ...(active === 'all' ? {} : { active: active === 'true' }),
            // Omitted when no sort is chosen, so the server's default order stands.
            ...(sortMember !== null && sortDirection !== null
              ? { sort: sortMember, order: sortDirection }
              : {}),
          },
          signal,
        )
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load products');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, search, active, sortMember, sortDirection],
  );

  // Debounce so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  /*
   * A server-scoped column filter re-queries, so it has to reset paging the
   * same way the search box does — otherwise narrowing to "Inactive" from
   * page 7 lands on a page the narrowed set may not have.
   */
  const previousActive = useRef(active);
  useEffect(() => {
    if (previousActive.current === active) return;
    previousActive.current = active;
    setPage(1);
  }, [active]);

  /*
   * Products are admin-only to write, enforced server-side regardless of the
   * permission set: the router carries `requireRole(...ADMIN_ROLES)`, so an
   * `advanced_user` holding `products.create` still gets a 403. Asking
   * `ability` rather than the role directly keeps this the one call site to
   * change if enforcement ever moves to the permission model.
   */
  const canCreate = ability.canCreate('products');
  const canUpdate = ability.canUpdate('products');
  const canDelete = ability.canDelete('products');
  const showActions = canUpdate || canDelete;
  /*
   * Editing which inclusions a product OFFERS is a product write, so it rides
   * on `canUpdate` — never the role, and never `/permissions/me`, which grants
   * `advanced_user` `products.update` that the router then refuses with a 403.
   * Like Edit and Delete, the control is hidden rather than disabled.
   */
  const canEditInclusions = canUpdate;
  /*
   * The quantity-band ladder is a product write too, so it rides on the same
   * grant as Edit and Inclusions — the router is `requireRole(...ADMIN_ROLES)`
   * on `PUT /products/{id}/inclusion-presets` exactly as it is on the other two.
   */
  const canEditPresets = canUpdate;
  /*
   * ⚠️ Three actions on `canUpdate` now — Edit, Inclusions, Presets. This feeds
   * `TableSkeleton`, so a stale count draws a loading lane narrower than the one
   * that replaces it and the whole table shifts as the rows land.
   */
  const actionCount = (canUpdate ? 3 : 0) + (canDelete ? 1 : 0);

  /*
   * The options every Travel product picker on this page shares — ONE request
   * for the whole page, not one per row (see the hook). Gated on `canUpdate`,
   * so a reader who cannot write the field spends nothing finding choices they
   * could never make.
   */
  const travelCatalogue = useTravelProductCatalogue(canUpdate);

  /**
   * Put the saved record back on the row.
   *
   * The PATCH returns the whole product with `travelProduct` already resolved
   * to `{id, productName}`, so the row takes the server's answer rather than
   * guessing a label from the option that was chosen. Re-reading the list
   * instead would spend a request and cost the page's scroll position for one
   * cell — and would race the debounced search effect.
   */
  const handleTravelProductSaved = useCallback((next: Product) => {
    setResult((current) =>
      current === null
        ? current
        : { ...current, data: current.data.map((row) => (row.id === next.id ? next : row)) },
    );
  }, []);
  /*
   * The table's floor, SUMMED from the one width table rather than written
   * down — so hiding a column really narrows the table instead of leaving it
   * padded out to a stale literal, and adding one cannot leave the floor behind.
   */
  const tableMinWidth = useMemo(
    () => renderKeys.reduce((sum, key) => sum + widthOf(key, showActions).px, 0),
    [renderKeys, showActions],
  );

  /*
   * ⚠️ **A COLUMN FILTER OUTLIVES ITS FUNNEL, and the funnel is the only way to
   * clear one.** Hiding a column is presentation only — the kanban column
   * control settled that and it is right: hiding must never change what is
   * fetched. But `visibleRows` goes on narrowing on a hidden column's filter,
   * and `FilterableTH`-style funnels exist only on rendered headers, so the rows
   * would sit narrowed with nothing on screen able to undo it. That is the
   * "filtered table under an unfiltered-looking heading" failure, one step
   * worse, because here the heading is not there at all.
   *
   * ⚠️ **`productActive` is the one column that would NOT have been stranded,
   * and that is not luck.** It is `scope: 'server'`, and the status filter in
   * the panel header reads and writes the very same filter state — a second
   * home that already exists for the sub-`md` card view, where every funnel
   * disappears too. So a hidden Status column is a case this page was already
   * built for: the dropdown keeps reading "Active only" and keeps offering "All
   * statuses", so the narrowing stays visible and stays clearable.
   *
   * It is covered by the same rule anyway, deliberately. A control that happens
   * to exist today is not a guarantee — and one rule that covers all nine
   * columns cannot quietly rot against the one column it was excused from.
   */
  const filteredHiddenColumns = COLUMN_KEYS.filter((key) => {
    if (layout.isVisible(key)) return false;
    const state = filters.headerFor(key).filter?.value;
    return state !== undefined && (state.selected.length > 0 || state.emptiness !== 'any');
  });

  const visibleRows = filters.visibleRows;
  const hiddenByFilters = rows.length - visibleRows.length;
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  // `initialLoad` is derived further up, beside the frozen-offset measurement.
  const filtered = search !== '' || active !== 'all' || filters.activeCount > 0;

  function clearFilters() {
    setSearch('');
    filters.clearAll();
    setPage(1);
  }

  function openDelete(product: Product) {
    setDeleteError(null);
    setPendingDelete(product);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteProduct(pendingDelete.id);
      setPendingDelete(null);
      /*
       * `d_products.travel_product_id` is ON DELETE SET NULL, so deleting a
       * product SILENTLY clears it off every other product that named it. The
       * reload below is what shows that, and the catalogue drops the option.
       */
      travelCatalogue.refresh();
      // Removing the only row on page 2+ would otherwise strand the user on an
      // empty final page; the page change refetches on its own.
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        // Already gone — the outcome the user wanted, so treat it as success.
        setPendingDelete(null);
        travelCatalogue.refresh();
        if (rows.length === 1 && page > 1) setPage((p) => p - 1);
        else await load();
      } else if (caught instanceof ApiError && caught.status === 409) {
        /*
         * f_deal_products.product_id is ON DELETE RESTRICT, so a product that
         * sits on any deal line cannot be deleted. The message names the count.
         * There is no endpoint that lists which deals hold it, so the best we
         * can offer is the pipeline itself — saying so is better than implying
         * a filter exists.
         */
        setDeleteError(
          <>
            {caught.message} Nothing was deleted — take it off those deals first.{' '}
            <Link to="/deals" className="font-medium underline underline-offset-2">Open the pipeline</Link>.
          </>,
        );
      } else if (caught instanceof ApiError && caught.status === 403) {
        setDeleteError(
          `${denialReason(ability, 'delete', 'products') ?? 'The server refused that delete.'} Nothing was deleted — ask an administrator.`,
        );
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  }

  function handleSaved(saved: Product, xeroPush?: ProductXeroPush) {
    const wasCreate = !formState?.product;
    setFormState(null);
    // A failed Xero Item push still saved the product; say so, leading with
    // "saved". `null` (pushed, skipped, or an older API) clears any old banner.
    setPushReport(productPushReport(saved, xeroPush));
    /*
     * The picker's option list is a page-level read, so a product created,
     * renamed or retired through the modal leaves it stale — and a stale
     * catalogue is invisible: a missing option reads as that product not
     * existing, and a renamed one keeps its old label.
     */
    travelCatalogue.refresh();
    // A new record lands at the top of `id DESC`, so send the user to page 1 to
    // see it rather than refreshing a page it isn't on.
    if (wasCreate && page !== 1) setPage(1);
    else void load();
  }

  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${total === 1 ? 'product' : 'products'}${search ? ` matching “${search}”` : ''}`;
  const hiddenLabel =
    hiddenByFilters > 0 ? ` — ${hiddenByFilters} hidden by column filters on this page` : '';

  const newProductButton = canCreate ? (
    <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
      New product
    </Button>
  ) : null;

  /**
   * One body cell, keyed the same way the header and the skeleton are.
   *
   * ⚠️ **A switch rather than a lookup table of JSX, so each cell keeps the
   * comment that belongs to it** — those comments are the record of why a cell
   * is shaped as it is, and a lookup object strands them from the markup they
   * describe.
   */
  function bodyCell(key: string, row: Product) {
    const frozen = frozenFor(key);
    switch (key) {
      case 'productName':
        return (
          <TD key={key} {...frozen}>
            <ProductName row={row} />
          </TD>
        );
      case 'productCategory':
        return (
          <TD key={key} className="text-xs text-fg-muted" {...frozen}>
            {orDash(row.productCategory)}
          </TD>
        );
      case 'travelProduct':
        return (
          <TD key={key} className="max-w-[11rem]" {...frozen}>
            <InlineTravelProduct
              product={row}
              catalogue={travelCatalogue}
              onSaved={handleTravelProductSaved}
            />
          </TD>
        );
      case 'productActive':
        return (
          <TD key={key} {...frozen}>
            <StatusBadge active={row.productActive} />
          </TD>
        );
      case 'unitPrice':
        return (
          <TD key={key} numeric className="text-fg-secondary" {...frozen}>
            <PriceCell value={row.unitPrice} unitLabel={row.unitLabel} />
          </TD>
        );
      case 'qtyOrdered':
        return (
          <TD key={key} numeric className="text-fg-secondary tabular" {...frozen}>
            {row.qtyOrdered}
          </TD>
        );
      case 'createdAt':
        return (
          <TD key={key} className="text-xs whitespace-nowrap text-fg-muted" {...frozen}>
            {formatDateOnly(row.createdAt)}
            <SourceLine value={row.createdBy} />
          </TD>
        );
      case XERO_KEY:
        return (
          <TD key={key} {...frozen}>
            <ProductXeroBadge product={row} />
          </TD>
        );
      case OFFERING_KEY:
        return (
          <TD key={key} {...frozen}>
            <OfferingCell
              product={row}
              state={offerings[row.id]}
              onCheck={() => checkOffering(row.id)}
            />
          </TD>
        );
      case ACTIONS_KEY:
        /* No `frozenFor`: `pinned` already IS frozen-right at offset 0, and the
           two mechanisms must not both land on one cell. */
        return (
          <TD key={key} numeric pinned>
            <div className="flex justify-end gap-1.5">
              {showActions ? (
                <>
                  {canUpdate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      leadingIcon={<PencilIcon />}
                      aria-label={`Edit ${row.productName}`}
                      title={`Edit ${row.productName}`}
                      onClick={() => setFormState({ product: row })}
                    />
                  )}
                  {canEditInclusions && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      leadingIcon={<ChecklistIcon />}
                      // The offering, not quantities — those are per line item
                      // and are set on the quote.
                      aria-label={`Choose which inclusions ${row.productName} offers`}
                      title={`Choose which inclusions ${row.productName} offers`}
                      onClick={() => setInclusionsFor(row)}
                    />
                  )}
                  {canEditPresets && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      leadingIcon={<BandsIcon />}
                      // What a line STARTS with at a given quantity — not what
                      // it may pick, which is the Inclusions button beside it.
                      aria-label={`Set inclusion presets by quantity for ${row.productName}`}
                      title={`Set inclusion presets by quantity for ${row.productName}`}
                      onClick={() => setPresetsFor(row)}
                    />
                  )}
                  {canDelete && (
                    <Button
                      variant="danger"
                      size="sm"
                      iconOnly
                      leadingIcon={<TrashIcon />}
                      aria-label={`Delete ${row.productName}`}
                      title={`Delete ${row.productName}`}
                      onClick={() => openDelete(row)}
                    />
                  )}
                </>
              ) : (
                <span className="text-xs text-fg-subtle">—</span>
              )}
            </div>
          </TD>
        );
      default:
        return null;
    }
  }

  const emptyState =
    rows.length === 0 ? (
      <ProductsEmptyState
        filtered={filtered}
        onClear={clearFilters}
        hasError={Boolean(error)}
        action={newProductButton}
      />
    ) : (
      <PageFilterEmptyState pageRows={rows.length} onClear={filters.clearAll} />
    );

  /*
   * ⚠️ Below every hook, so the early return cannot change the hook order. The
   * refusal is rendered here rather than by the route, which carries no guard.
   */
  if (!canReadModule) {
    return (
      <>
        <PageHeader eyebrow="Catalog" title="Products" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Catalog"
        title="Products"
        actions={newProductButton}
      />

      <Panel>
        <PanelHeader
          title="All products"
          description={countLabel}
          actions={
            // `sm:flex-wrap` so a third control cannot push this bank past the
            // header: `PanelHeader` wraps `actions` in a `shrink-0` flex row, so
            // an over-wide group there has nowhere to go.
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <div className="w-full sm:w-40">
                <Field label="Filter by status" hideLabel>
                  {({ id, 'aria-describedby': describedBy }) => (
                    <Combobox
                      id={id}
                      /* `'all'` is a real `ActiveFilter` member and `''` is the
                         Combobox's absent state — mapped both ways here, and
                         nowhere else, so `changeActive` keeps taking the union
                         it was written for. */
                      value={active === 'all' ? '' : active}
                      onChange={(next) => changeActive(next === '' ? 'all' : (next as ActiveFilter))}
                      options={ACTIVE_PICKER_OPTIONS}
                      emptyOptionLabel={ALL_STATUSES_LABEL}
                      placeholder={ALL_STATUSES_LABEL}
                      searchPlaceholder="Search statuses…"
                      aria-describedby={describedBy}
                    />
                  )}
                </Field>
              </div>
              <div className="w-full sm:w-64">
                <Field label="Search products" hideLabel>
                  {({ id }) => (
                    <Input
                      id={id}
                      type="search"
                      // The API matches on product_name only, so promising more
                      // in the placeholder would be a lie the results expose.
                      placeholder="Search product name…"
                      leadingIcon={<SearchIcon />}
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  )}
                </Field>
              </div>
              {/*
                ⚠️ **`md`, not `sm`, because that is where the TABLE is.** Below
                `md` this page is a card list: there are no columns to show,
                hide, reorder or freeze, so the control would open a manager that
                governs nothing on screen. The status and search controls above
                stay at every width because they govern the DATA, which the cards
                show too.
              */}
              <div className="hidden md:block">
                <ColumnManager
                  layout={layout}
                  labelOf={labelOfColumn}
                  lockedKeys={LOCKED_KEYS}
                  pinnedKeys={PINNED_KEYS}
                  lockReasonOf={lockReasonOfColumn}
                />
              </div>
            </div>
          }
        />

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
          {hiddenLabel}
        </p>

        {pushReport && (
          <XeroPushReportBanner report={pushReport} onDismiss={() => setPushReport(null)} />
        )}

        {error && (
          <Notice
            tone="danger"
            role="alert"
            title={<>Couldn&rsquo;t load products.</>}
            className="mx-4 my-3"
          >
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {/* Below md a seven-column table cannot fit 320px, so rows become cards. */}
          <div className="md:hidden">
            {initialLoad ? (
              <ListSkeleton />
            ) : visibleRows.length === 0 ? (
              emptyState
            ) : (
              <ul className="m-0 list-none p-0">
                {visibleRows.map((row) => (
                  <li key={row.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    <div className="flex items-start justify-between gap-3">
                      <ProductName row={row} />
                      <StatusBadge active={row.productActive} />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                      <span className="text-fg-muted">{orDash(row.productCategory)}</span>
                      <span className="shrink-0 text-fg-secondary tabular">
                        <PriceCell value={row.unitPrice} unitLabel={row.unitLabel} /> · qty{' '}
                        {row.qtyOrdered}
                      </span>
                    </div>
                    {/* Wraps rather than sitting in a row, so 320px never
                        squeezes the label against a long product name. */}
                    <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="text-2xs text-fg-muted">Travel product</span>
                      <div className="min-w-0 flex-1">
                        <InlineTravelProduct
                          product={row}
                          catalogue={travelCatalogue}
                          onSaved={handleTravelProductSaved}
                        />
                      </div>
                    </div>
                    <p className="mt-1 text-2xs text-fg-subtle">
                      Created {formatDateOnly(row.createdAt)}
                      {row.createdBy ? ` by ${row.createdBy}` : ''}
                    </p>
                    {/* Same column as the desktop "In Xero"; absent on an older
                        API, so the whole line is skipped rather than labelled
                        over nothing. Inline error: a Tooltip shows nothing on touch. */}
                    {row.xeroSync !== undefined && (
                      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-2xs text-fg-muted">{SPECLESS_LABELS[XERO_KEY]}</span>
                        <div className="min-w-0">
                          <ProductXeroBadge product={row} inlineError />
                        </div>
                      </div>
                    )}
                    {/* Wraps rather than sitting in a row, so 320px never
                        squeezes the label against the control. */}
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-2xs text-fg-muted">Inclusions offered</span>
                      <div className="min-w-0">
                        <OfferingCell
                          product={row}
                          state={offerings[row.id]}
                          onCheck={() => checkOffering(row.id)}
                        />
                      </div>
                    </div>
                    {showActions && (
                      <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">
                        {canUpdate && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<PencilIcon />}
                            onClick={() => setFormState({ product: row })}
                          >
                            Edit
                          </Button>
                        )}
                        {canEditInclusions && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<ChecklistIcon />}
                            onClick={() => setInclusionsFor(row)}
                          >
                            Inclusions
                          </Button>
                        )}
                        {canEditPresets && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<BandsIcon />}
                            onClick={() => setPresetsFor(row)}
                          >
                            Presets
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="danger"
                            size="sm"
                            leadingIcon={<TrashIcon />}
                            onClick={() => openDelete(row)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* All three mounts take the SAME `renderKeys`, the same `frozenFor`
              and the same computed floor, so a hidden, reordered or frozen
              column cannot render one way while loading and another once the
              data lands. That agreement used to be three hand-kept lists and
              three copies of a `min-w-` literal. */}
          <div className="hidden md:block">
            {initialLoad ? (
              <ProductsTable minWidth={tableMinWidth}>
                <ProductsTableHead
                  showActions={showActions}
                  headerFor={headerFor}
                  keys={renderKeys}
                  frozenFor={frozenFor}
                  headRowRef={headRowRef}
                />
                <TableSkeleton actions={actionCount} keys={renderKeys} frozenFor={frozenFor} />
              </ProductsTable>
            ) : visibleRows.length === 0 ? (
              <>
                {/* The headers stay mounted so the filter that emptied the page
                    is still reachable to undo. */}
                {rows.length > 0 && (
                  <ProductsTable minWidth={tableMinWidth}>
                    <ProductsTableHead
                      showActions={showActions}
                      headerFor={headerFor}
                      keys={renderKeys}
                      frozenFor={frozenFor}
                      headRowRef={headRowRef}
                    />
                  </ProductsTable>
                )}
                {emptyState}
              </>
            ) : (
              <ProductsTable minWidth={tableMinWidth}>
                <ProductsTableHead
                  showActions={showActions}
                  headerFor={headerFor}
                  keys={renderKeys}
                  frozenFor={frozenFor}
                  headRowRef={headRowRef}
                />
                <TBody>
                  {visibleRows.map((row) => (
                    <TR key={row.id} interactive>
                      {renderKeys.map((key) => bodyCell(key, row))}
                    </TR>
                  ))}
                </TBody>
              </ProductsTable>
            )}
          </div>
        </div>

        {result && (
          <PanelFooter>
            <div className="flex min-w-0 flex-wrap items-center justify-center gap-x-2 gap-y-1 sm:justify-start">
              <p className="m-0 text-xs text-fg-muted tabular">
                {total === 0 ? 'No products to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
                {hiddenByFilters > 0 && (
                  <span className="text-fg-subtle">
                    {' '}
                    · {hiddenByFilters} hidden by column filters
                  </span>
                )}
              </p>
              {/*
                The way back out when the funnel that set a filter is no longer
                on screen — see `filteredHiddenColumns`. It renders ONLY while a
                HIDDEN column carries one: a visible column already has its
                funnel, and a second control for the same job is noise the reader
                has to reconcile. The count beside it is what makes the narrowing
                visible; this is what makes it undoable.
              */}
              {filteredHiddenColumns.length > 0 && (
                <Button variant="secondary" size="sm" onClick={filters.clearAll}>
                  Clear column filters
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>

      <ProductFormModal
        open={formState !== null}
        onClose={() => setFormState(null)}
        product={formState?.product}
        onSaved={handleSaved}
      />

      {/*
        Mounted only while a product is chosen, because `product` is required
        on this dialog — unlike `ProductFormModal`, whose `product` is
        optional (absent means "create") and which stays mounted with `open`.

        ⚠️ `onSaved` is NOT `void load()`. `Product` carries no inclusions
        field, so re-reading the list would spend a request and change nothing
        on screen — only one cached per-row offering went stale, so exactly
        that row is re-read.
      */}
      {inclusionsFor && (
        <ProductInclusionsModal
          product={inclusionsFor}
          open
          onClose={() => setInclusionsFor(null)}
          onSaved={() => {
            refreshOffering(inclusionsFor.id);
            setInclusionsFor(null);
          }}
        />
      )}

      {/*
        Mounted only while a product is chosen, like the dialog above it.

        ⚠️ `onSaved` deliberately refreshes NOTHING. The offering cell shows what
        a product OFFERS, which this dialog does not write — it writes the
        quantity-band ladder, and no column on this page renders one. Calling
        `refreshOffering` here would spend a request to redraw a cell that cannot
        have changed; calling `load()` would spend one and cost the scroll
        position too.
      */}
      {presetsFor && (
        <ProductInclusionPresetsModal
          product={presetsFor}
          open
          onClose={() => setPresetsFor(null)}
          onSaved={() => setPresetsFor(null)}
        />
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={pendingDelete ? `Delete ${pendingDelete.productName}?` : 'Delete product?'}
        description="Only possible if no deal has it on a line. Cannot be undone."
        confirmLabel="Delete product"
        loading={deleting}
        error={deleteError}
      />
    </>
  );
}

/**
 * Distinct from "no products": the server returned rows and the column filters
 * — which only ever see the loaded page — hid all of them. Saying so is the
 * whole point; otherwise it reads as an empty catalogue.
 */
function PageFilterEmptyState({ pageRows, onClear }: { pageRows: number; onClear: () => void }) {
  return (
    <EmptyState
      icon={<TagIcon />}
      title="No rows on this page match the column filters"
      description={`Filters apply to the ${pageRows} rows loaded here. Try another page.`}
      action={
        <Button variant="secondary" size="sm" onClick={onClear}>
          Clear column filters
        </Button>
      }
    />
  );
}

function ProductsEmptyState({
  filtered,
  onClear,
  hasError,
  action,
}: {
  filtered: boolean;
  onClear: () => void;
  hasError: boolean;
  action: ReactNode;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<TagIcon />}
        title="Nothing to show"
        description="The product list couldn’t be loaded — see the message above."
      />
    );
  }

  return (
    <EmptyState
      icon={<TagIcon />}
      title={filtered ? 'No products match those filters' : 'No products yet'}
      description={
        filtered
          ? 'Try a different name, or clear the filters.'
          : 'Add the first product.'
      }
      action={
        filtered ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        ) : (
          action
        )
      }
    />
  );
}
